import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import ChatHeader from './ChatHeader';
import ChatBody from './ChatBody';

import ErrorBoundary from './ErrorBoundary';
import * as Axios from 'axios';

// Import custom styles for WhatsApp chat
import './styles/_whatsapp-chat.scss';
import { subMinutes } from 'date-fns';

/**
 * Enhanced WhatsApp Chat Modal Component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.activity - The selected chat activity
 * @param {Object} props.user - The user/lead information
 */
const WhatsAppChatModal = ({ isOpen, onClose, activity, user }) => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filterDate, setFilterDate] = useState(null);
    const [filterEndDate, setFilterEndDate] = useState(null);

    // Initialize filter date when activity changes
    useEffect(() => {
        if (activity?.date && !filterDate) {
            const activityDate = new Date(activity.date);
            setFilterDate(activityDate);
        }
    }, [activity, filterDate]);

    const fetchChatMessages = useCallback(async () => {
        if (!activity || !user) {
            setError("Missing activity or user data.");
            setMessages([]);
            setIsLoading(false);
            return;
        }

        // Determine the formatted dates
        let formattedFromDate, formattedToDate;
        if (activity.date && !filterDate) {
            const activityDate = subMinutes(new Date(activity.createdAt ?? activity.date), 15);
            formattedFromDate = activityDate.toISOString();
            if (!filterDate) {
                setFilterDate(activityDate);
            }
        } else if (filterDate) {
            formattedFromDate = filterDate.toISOString();
        }

        if (filterEndDate) {
            formattedToDate = filterEndDate.toISOString();
        }

        // Validate required data
        const agentNo = activity?.assigned_agent?.user?.phone;
        const leadNo = user?.phone;

        if (!agentNo) {
            setError("Agent number is missing or could not be determined.");
            setMessages([]);
            setIsLoading(false);
            return;
        }
        if (!leadNo) {
            setError("Lead's phone number is missing.");
            setMessages([]);
            setIsLoading(false);
            return;
        }

        // Clean phone numbers
        const cleanAgentNo = agentNo.replaceAll('+', '');
        const cleanLeadNo = leadNo.replaceAll('+', '');

        // Set loading state
        setIsLoading(true);
        setError(null);
        setMessages([]);

        // Create AbortController for request cancellation
        const abortController = new AbortController();

        try {
            const response = await Axios.get(
                `https://whatsapp.alba.homes/session/${cleanAgentNo}/messages/${cleanLeadNo}@c.us`,
                {
                    params: {
                        from: formattedFromDate,
                        to: formattedToDate,
                    },
                    signal: abortController.signal
                }
            );

            const fetchedMessages = response.data || [];
            setMessages(fetchedMessages);
            setIsLoading(false);
        } catch (err) {
            if (err.name === 'AbortError') {
                // Request was cancelled, don't update state
                return;
            }
            console.error('Error fetching chat history:', err);
            setError(err.response?.data?.message || err.message || 'Failed to fetch chat history.');
            setMessages([]);
            setIsLoading(false);
        }

        // Return cleanup function
        return () => {
            abortController.abort();
        };
    }, [activity, user, filterDate, filterEndDate]);

    // Fetch messages when modal opens or filter changes
    useEffect(() => {
        let cleanup;
        if (isOpen && activity) {
            cleanup = fetchChatMessages();
        }

        return () => {
            if (cleanup && typeof cleanup === 'function') {
                cleanup();
            }
        };
    }, [isOpen, activity, user, filterDate, filterEndDate, fetchChatMessages]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Reset all state when component unmounts
            setMessages([]);
            setError(null);
            setIsLoading(false);
            setFilterDate(null);
            setFilterEndDate(null);
        };
    }, []);

    const handleFilterChange = (newFilterDate, newFilterEndDate) => {
        setFilterDate(newFilterDate);
        setFilterEndDate(newFilterEndDate);
    };

    const handleRefresh = () => {
        fetchChatMessages();
    };

    const handleClose = () => {
        // Reset state when closing
        setMessages([]);
        setError(null);
        setIsLoading(false);
        setFilterDate(null);
        setFilterEndDate(null);
        onClose();
    };

    if (!activity || !user) {
        return null;
    }

    return (
        <ErrorBoundary>
            <Modal
                isOpen={isOpen}
                toggle={handleClose}
                size="lg"
                backdrop="static"
                className="whatsapp-chat-modal"
                centered
            >
                <ChatHeader
                    user={user}
                    agent={activity?.assigned_agent?.user}
                    onClose={handleClose}
                />

                <ModalBody className="p-0">
                    <ChatBody
                        messages={messages}
                        isLoading={isLoading}
                        error={error}
                        filterDate={filterDate}
                        filterEndDate={filterEndDate}
                        onFilterChange={handleFilterChange}
                        onRefresh={handleRefresh}
                        user={user}
                        activity={activity}
                    />
                </ModalBody>

            </Modal>
        </ErrorBoundary>
    );
};

WhatsAppChatModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    activity: PropTypes.shape({
        date: PropTypes.string,
        assigned_agent: PropTypes.shape({
            user: PropTypes.shape({
                phone: PropTypes.string,
                first_name: PropTypes.string,
                last_name: PropTypes.string
            })
        })
    }),
    user: PropTypes.shape({
        phone: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        photo: PropTypes.string
    })
};

export default WhatsAppChatModal;